import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useEditFoundingRates } from "./use-founding-mutations";
import { MAX_DISCOUNT_PERCENT, MIN_DISCOUNT_PERCENT } from "./constants";
import type { EditFoundingRatesBody } from "./founding-api";
import type { FoundingEnrollmentDetail } from "./types";

interface EditFoundingRatesDialogProps {
  readonly propertyId: string;
  readonly enrollment: FoundingEnrollmentDetail;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onDone: () => void;
}

/** Prefill string for a percent field (empty when the source value is null). */
function toInput(value: number | null): string {
  return value == null ? "" : String(value);
}

interface FieldState {
  readonly changed: boolean;
  readonly valid: boolean;
  readonly value: number;
}

/** Compare an edited percent field against its prefilled original. */
function fieldState(current: string, original: string): FieldState {
  const value = Number(current);
  return {
    changed: current.trim() !== original.trim(),
    valid:
      Number.isInteger(value) && value >= MIN_DISCOUNT_PERCENT && value <= MAX_DISCOUNT_PERCENT,
    value,
  };
}

/**
 * Per-property founding rate override (ADR-0010, ADMIN only). The editable
 * fields follow the enrollment's SHAPE, not its program: a two-phase enrollment
 * (intro Year 1 + ongoing Year 2+) exposes both rates — including a two-phase
 * PORTFOLIO_CUSTOM — while a flat enrollment exposes the single flat rate. Only
 * the fields the admin actually changed are sent, and the change takes effect at
 * the property's NEXT RENEWAL (surfaced in the hint).
 */
export function EditFoundingRatesDialog({
  propertyId,
  enrollment,
  open,
  onOpenChange,
  onDone,
}: EditFoundingRatesDialogProps) {
  const { t } = useTranslation();
  // Branch on shape, not program. A FLAT phase (or a non-null flatPercent) is a
  // single flat rate; anything else is the two-phase intro/ongoing schedule.
  const isFlat = enrollment.phase === "FLAT" || enrollment.flatPercent != null;
  const isTwoPhase = !isFlat;

  const initialIntro = toInput(enrollment.introPercent);
  const initialOngoing = toInput(enrollment.ongoingPercent);
  const initialFlat = toInput(enrollment.flatPercent);

  const [intro, setIntro] = useState(initialIntro);
  const [ongoing, setOngoing] = useState(initialOngoing);
  const [flat, setFlat] = useState(initialFlat);
  const [error, setError] = useState<string | null>(null);
  const editRates = useEditFoundingRates();

  useEffect(() => {
    if (open) {
      setIntro(initialIntro);
      setOngoing(initialOngoing);
      setFlat(initialFlat);
      setError(null);
    }
    // Reset from the enrollment's values every time the dialog (re)opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const introField = fieldState(intro, initialIntro);
  const ongoingField = fieldState(ongoing, initialOngoing);
  const flatField = fieldState(flat, initialFlat);

  const anyChanged = isTwoPhase ? introField.changed || ongoingField.changed : flatField.changed;
  const editedAllValid = isTwoPhase
    ? (!introField.changed || introField.valid) && (!ongoingField.changed || ongoingField.valid)
    : !flatField.changed || flatField.valid;
  const canSubmit = anyChanged && editedAllValid && !editRates.isPending;

  const handleClose = (next: boolean) => {
    if (!next && editRates.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    setError(null);
    const body: EditFoundingRatesBody = { propertyId };
    if (isTwoPhase) {
      if (introField.changed) body.introPercent = introField.value;
      if (ongoingField.changed) body.ongoingPercent = ongoingField.value;
    } else if (flatField.changed) {
      body.flatPercent = flatField.value;
    }

    editRates.mutate(body, {
      onSuccess: () => onDone(),
      onError: (err) => {
        setError(
          resolveMutationErrorMessage(err, t, {
            forbiddenKey: "founding.errors.forbidden",
            serverPrefixKey: "founding.errors.serverPrefix",
            genericKey: "founding.errors.generic",
          }),
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="edit-founding-rates-dialog">
        <DialogHeader>
          <DialogTitle>{t("founding.editRates.title")}</DialogTitle>
          <DialogDescription data-testid="edit-rates-hint">
            {t("founding.editRates.nextRenewalHint")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isTwoPhase ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="edit-rates-intro">{t("founding.editRates.introPercent")}</Label>
                <Input
                  id="edit-rates-intro"
                  data-testid="edit-rates-intro"
                  type="number"
                  min={MIN_DISCOUNT_PERCENT}
                  max={MAX_DISCOUNT_PERCENT}
                  value={intro}
                  onChange={(e) => setIntro(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-rates-ongoing">{t("founding.editRates.ongoingPercent")}</Label>
                <Input
                  id="edit-rates-ongoing"
                  data-testid="edit-rates-ongoing"
                  type="number"
                  min={MIN_DISCOUNT_PERCENT}
                  max={MAX_DISCOUNT_PERCENT}
                  value={ongoing}
                  onChange={(e) => setOngoing(e.target.value)}
                />
              </div>
            </>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="edit-rates-flat">{t("founding.editRates.flatPercent")}</Label>
              <Input
                id="edit-rates-flat"
                data-testid="edit-rates-flat"
                type="number"
                min={MIN_DISCOUNT_PERCENT}
                max={MAX_DISCOUNT_PERCENT}
                value={flat}
                onChange={(e) => setFlat(e.target.value)}
              />
            </div>
          )}
          {error && (
            <p data-testid="edit-rates-error" className="font-sans text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={editRates.isPending}
          >
            {t("founding.editRates.cancel")}
          </Button>
          <Button
            type="button"
            data-testid="edit-rates-submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {t("founding.editRates.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
